import { useSelector } from "react-redux";
import { useState, useEffect, memo } from "react";
import { Text, Accordion } from "@chakra-ui/react";
import { Tab, TabList, TabPanel, TabPanels, TabGroup } from "@headlessui/react";
import EditSectionPracticeAreas from "./common-screens/EditSectionPracticeAreas";
import EditSectionPractitionerModalities from "./common-screens/EditSectionPractitionerModalities";
import {
  ViewSectionAbout,
  ViewSectionAvailability,
  ViewSectionClientGroups,
  ViewSectionOfficeMap,
  ViewSectionPracticeAreas,
  ViewSectionPracticeTypes,
  ViewSectionProfileHeader,
  ViewSectionQualifications,
  ViewSectionSessionType,
} from "../../../practitioner-profile-view";
import { renderAddresses } from "../../../utilities/render-helpers/renderPractitionerProfile";
import ProfileCompletion from "./common-screens/ProfileCompletion";
import { IsMobileView } from "../../../utilities/helpers";
import EditAssociations from "./common-screens/EditAssociations";
import OptionalSearch from "./common-screens/OptionalSearch";
import EditSectionClientGroups from "./common-screens/EditSectionClientGroups";
import EditSectionAbout from "./common-screens/EditSectionAbout";
import EditSectionAvailability from "./common-screens/EditSectionAvailability";
import EditSectionQualifications from "./common-screens/EditSectionQualifications";
import EditSectionSessionType from "./common-screens/EditSectionSessionType";
import EditSectionContact from "./common-screens/EditSectionContact";
import EditSectionFees from "./common-screens/EditSectionFees";
import EditSectionProfileImage from "./common-screens/EditSectionProfileImage";
import EditSectionProfileBanner from "./common-screens/EditSectionProfileBanner";
import EditSectionProfileVideo from "./common-screens/EditSectionProfileVideo";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import { ViewSectionAssociations } from "../../../practitioner-profile-view/ViewSectionAssocations";
import { ViewSectionFees } from "../../../practitioner-profile-view/ViewSectionFees";
import { useSearchParams } from "next/navigation";

const tabMap = ["edit", "advanced", "view", "verification"];

const EditPractitionerProfile = () => {
  const profile = useSelector(
    (state) => state?.user?.userDetails?.therapistProfile,
  );
  const searchParams = useSearchParams();
  const [loaded, setLoaded] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (profile?.contactEmail?.length === 0 || profile.contactEmail === null) {
      setLoaded(false);
    } else {
      setLoaded(true);
    }
  }, [profile]);

  useEffect(() => {
    if (!tabMap.includes(searchParams.get("tab"))) {
      return;
    }
    setTabIndex(tabMap.indexOf(searchParams.get("tab")));
  }, [searchParams]);

  const mobileView = IsMobileView();

  return !loaded ? (
    <LoadingSpinner />
  ) : (
    <div className="flex-1 flex flex-col items-center w-full bg-[#F2F1EF] p-4">
      <TabGroup
        selectedIndex={tabIndex}
        onChange={setTabIndex}
        className="w-full max-w-[1148px]"
      >
        <TabList className="flex space-x-1">
          <Tab
            className={({
              selected,
            }) => `text-brand font-bold w-full rounded-t-lg py-2.5 leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
            ${selected ? "bg-white text-[#587500]" : "hover:bg-white/[0.12] hover:text-brand-text"}
          `}
          >
            Edit profile
          </Tab>
          <Tab
            className={({
              selected,
            }) => `text-brand font-bold w-full rounded-t-lg py-2.5 leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
            ${selected ? "bg-white text-[#587500]" : "hover:bg-white/[0.12] hover:text-brand-text"}
          `}
          >
            Advanced filters
          </Tab>
          <Tab
            className={({
              selected,
            }) => `text-brand font-bold w-full rounded-t-lg py-2.5 leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
            ${selected ? "bg-white text-[#587500]" : "hover:bg-white/[0.12] hover:text-brand-text"}
          `}
          >
            View profile
          </Tab>
          {/* <Tab
            className={({
              selected,
            }) => `text-brand font-bold w-full rounded-t-lg py-2.5 leading-5 ring-white/60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2
            ${selected ? "bg-white text-[#587500]" : "hover:bg-white/[0.12] hover:text-brand-text"}
          `}
          >
            Manage verification
          </Tab> */}
        </TabList>

        <TabPanels className="rounded-b-lg bg-white w-full">
          <TabPanel className="w-full flex flex-col">
            <ProfileCompletion
              profile={profile}
              onView={() => setTabIndex(2)}
            />
            <div className="w-full bg-brand">
              <Text textStyle="h4" padding="20px 16px 0px 16px" color="white">
                Required
              </Text>
              <Text
                textStyle="footer2"
                padding="0px 16px 20px 16px"
                color="white"
              >
                The fields below must be completed before we can market your
                profile to our clients
              </Text>
            </div>

            <Accordion allowMultiple width="100%">
              <EditSectionProfileImage profile={profile} />
              <EditSectionContact profile={profile} />
              <EditSectionAbout profile={profile} />
              <EditSectionAvailability profile={profile} />
              <EditSectionPracticeAreas profile={profile} />
              <EditSectionPractitionerModalities profile={profile} />
              <EditSectionSessionType profile={profile} />
              <EditSectionClientGroups profile={profile} />
              <EditSectionFees profile={profile} />
              <EditSectionQualifications profile={profile} />
            </Accordion>
            <div className="w-full bg-brand">
              <Text textStyle="h4" padding="20px 16px 0px 16px" color="white">
                Recommended
              </Text>
              <Text
                textStyle="footer2"
                padding="0px 20px 20px 20px"
                color="white"
              >
                The fields below are optional but including them will help your
                profile stand out
              </Text>
            </div>
            <Accordion allowMultiple width="100%">
              <EditSectionProfileBanner profile={profile} />
              <EditSectionProfileVideo profile={profile} />
            </Accordion>
          </TabPanel>
          <TabPanel className="w-full">
            {profile?.outOfOffice && (
              <ProfileCompletion
                profile={profile}
                onView={() => setTabIndex(2)}
              />
            )}
            <OptionalSearch profile={profile} />
          </TabPanel>
          <TabPanel className="w-full p-4">
            {profile?.outOfOffice && (
              <ProfileCompletion
                profile={profile}
                onView={() => setTabIndex(2)}
              />
            )}
            <div className="flex gap-5 w-full justify-center">
              <div className="w-full flex flex-col gap-5 max-w-[709px]">
                <ViewSectionProfileHeader profile={profile} />

                <ViewSectionAbout profile={profile} />
                {mobileView && (
                  <>
                    {renderAddresses.length > 0 && (
                      <ViewSectionOfficeMap profile={profile} />
                    )}

                    <ViewSectionSessionType profile={profile} />
                  </>
                )}
                <ViewSectionPracticeAreas profile={profile} />
                <ViewSectionFees profile={profile} />
                <ViewSectionAvailability profile={profile} />
                <ViewSectionPracticeTypes profile={profile} />
                <ViewSectionClientGroups profile={profile} />
                <ViewSectionQualifications profile={profile} />
                <ViewSectionAssociations profile={profile} />
              </div>
              {!mobileView && (
                <div className="w-[420px] flex flex-col gap-2">
                  <div className="bg-white rounded-[20px] w-full">
                    <ViewSectionSessionType profile={profile} />
                  </div>
                  {renderAddresses.length > 0 && (
                    <div className="bg-white rounded-[20px] w-full">
                      <ViewSectionOfficeMap profile={profile} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabPanel>
          {/* <TabPanel className='w-full p-4'>
            <EditAssociations practitionerType={'therapist'} />
          </TabPanel> */}
        </TabPanels>
      </TabGroup>
    </div>
  );
};

export default memo(EditPractitionerProfile);
